import { formatLayout } from '@trm/datetime';
import { DateTime } from 'effect';
import { useState } from 'react';
import { useAvailableBetaPages, useTryBeta } from '~/beta/betaHooks';
import { type BetaPageId, betaPages } from '~/beta/betaPages';
import useAuth from '~/hooks/useAuth';
import { PagePolicies } from '~/utils/roles';

import type { WhatsNewEntry } from './components/WhatsNewPanel';
import { type ReleaseNote, releaseNotes } from './releaseNotes';
import { useSeenNotesStore } from './seenNotesStore';

const formatPublishedOn = ({ publishedOn: { year, month, day } }: ReleaseNote) =>
  formatLayout(DateTime.makeUnsafe({ year, month, day }), 'MMM D, YYYY');

const isVisible =
  (hasBetaAccess: boolean, available: Record<BetaPageId, boolean>) => (note: ReleaseNote) => {
    if (note.audience === 'beta' && !hasBetaAccess) return false;
    if (note.betaPage === undefined) return true;

    return available[note.betaPage];
  };

export const visibleNotes = (
  notes: readonly ReleaseNote[],
  hasBetaAccess: boolean,
  available: Record<BetaPageId, boolean>,
) => notes.filter(isVisible(hasBetaAccess, available));

export const useWhatsNew = () => {
  const { hasPermission } = useAuth();
  const seen = useSeenNotesStore((state) => state.seen);
  const markSeen = useSeenNotesStore((state) => state.markSeen);
  const tryBeta = useTryBeta();
  const available = useAvailableBetaPages();
  const [isOpen, setIsOpen] = useState(false);

  const notes = visibleNotes(releaseNotes, hasPermission(PagePolicies.BetaPages), available);

  const close = () => {
    markSeen(notes.map((note) => note.id));
    setIsOpen(false);
  };

  const tryAction = (page: BetaPageId | undefined) => {
    if (page === undefined) return null;
    if (betaPages[page].switching.kind !== 'route') return null;

    return () => {
      close();
      tryBeta(page);
    };
  };

  const entries: readonly WhatsNewEntry[] = notes.map((note) => ({
    id: note.id,
    date: formatPublishedOn(note),
    title: note.title,
    body: note.body,
    isNew: !seen.includes(note.id),
    isBeta: note.audience === 'beta',
    onTry: tryAction(note.betaPage),
  }));

  return {
    entries,
    unreadCount: entries.filter((entry) => entry.isNew).length,
    isOpen,
    open: () => setIsOpen(true),
    close,
  };
};
